import { useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router"
import { ArrowLeft, ArrowUpRight, CalendarDate, Flag01, File02, Phone01 } from "@untitledui/icons"
import { toast } from "sonner"

import type { GhlContactDetail } from "@/lib/api/ghl-types"
import {
  useCreateNote,
  useCreateTask,
  useDecide,
  useGhlContact,
  useGhlPipelines,
  useGhlUsers,
  usePatchContact,
  usePatchOpportunity,
  usePatchTask,
} from "@/lib/api/use-ghl"
import { TextSelect } from "@/components/ui/text-select"
import { cx } from "@/lib/utils/cx"
import { BookDialog } from "./book-dialog"
import { Check } from "./contacts"
import { FOLLOW_UP } from "./follow-up"
import { Avatar, ChannelIcon, DARK_BTN, Empty, INPUT, LIGHT_BTN, StatusPill, TagChip, fmtDate, fmtDateTime, pill, relTime, usd, userName } from "./shared"
import { ThreadPanel } from "./thread-panel"

// The contact record: who they are and what to do next at the top, the
// conversation as the main thing, and one quiet rail with the facts —
// vehicle, estimate, deal, owner, consent, tasks, notes, visits. Only
// what the data says; nothing decorative.

export function ContactRecord({ teamId, contactId }: { teamId: number | string; contactId: string }) {
  const { data, isLoading } = useGhlContact(teamId, contactId)
  const { data: users = [] } = useGhlUsers(teamId)
  const { data: pipelines = [] } = useGhlPipelines(teamId)
  const patch = usePatchContact(teamId)
  const decide = useDecide(teamId)
  const [params] = useSearchParams()
  const [view, setView] = useState<"conversation" | "everything">("conversation")
  const [booking, setBooking] = useState(false)
  const composerRef = useRef<HTMLTextAreaElement>(null)

  if (isLoading || !data) return <Empty>{isLoading ? "Loading…" : "Contact not found."}</Empty>
  const { contact: c, summary: s } = data
  const name = `${c.firstName} ${c.lastName}`.trim()
  const fu = FOLLOW_UP[s.followUp]
  const stages = pipelines[0]?.stages ?? []
  const smsBlocked = !c.phone || c.dndSettings.SMS.status === "active"
  const focusComposer = () => { setView("conversation"); window.setTimeout(() => composerRef.current?.focus(), 50) }

  const run = async (d: Parameters<typeof decide.mutateAsync>[0], fallback: string) => {
    try { const r = await decide.mutateAsync(d); toast.success(r.note ?? fallback); return true } catch { toast.error("Couldn't do that — try again"); return false }
  }

  // The one line that says where this person is, and the one button.
  const lastInbound = [...data.messages].reverse().find((m) => m.direction === "inbound")
  const next: { line: string; button?: React.ReactNode } = (() => {
    switch (s.followUp) {
      case "waiting": return { line: lastInbound ? `Wrote ${relTime(lastInbound.dateAdded)}: “${lastInbound.body}”` : "Waiting on a reply.", button: <button type="button" onClick={focusComposer} className={DARK_BTN}>Reply</button> }
      case "to_confirm": return { line: "A new estimate is waiting for your confirm.", button: <button type="button" onClick={() => s.estimateId != null && void run({ kind: "confirm", contactId: c.id, estimateId: s.estimateId }, "Confirmed")} disabled={decide.isPending} className={DARK_BTN}>Confirm and start follow-up</button> }
      case "to_call": return { line: `${s.nextTouchTitle ?? `Call ${c.firstName}`} · ${s.nextTouchAt && new Date(s.nextTouchAt).getTime() < Date.now() ? "overdue" : "due today"}.`, button: <Link to="/marketing" className={DARK_BTN}>Log the call in Today</Link> }
      case "booked": return { line: s.bookedAt ? `Booked for ${fmtDateTime(s.bookedAt)}.` : "Scheduled." }
      case "following": return { line: s.nextTouchAt ? `Next: ${s.nextTouchTitle ?? "follow-up"} · ${fmtDate(s.nextTouchAt)}.` : "Automated follow-up is running.", button: <button type="button" onClick={() => setBooking(true)} className={DARK_BTN}>Book {c.firstName}</button> }
      case "won": return { line: `Won${s.dealValue != null ? ` · ${usd(s.dealValue)}` : ""}.` }
      case "passed": return { line: "Passed." }
      case "no_contact": return { line: "No phone or email on file — nothing can go out." }
      default: return { line: "No open deal." }
    }
  })()

  return (
    <div>
      <Link to="/marketing/contacts" className="inline-flex items-center gap-1.5 rounded-sm text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30">
        <ArrowLeft className="size-3.5" aria-hidden /> Contacts
      </Link>

      {/* Who, where they are, what to do */}
      <header className="mt-3 rounded-xl border border-zinc-200 bg-white px-5 py-4">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar name={name} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-[22px] font-semibold tracking-tight text-zinc-900">{name}</h2>
              <span className={cx("inline-flex items-center gap-1.5 text-[13px] font-semibold", fu.text)}><span className={cx("size-2 rounded-full", fu.dot)} aria-hidden />{fu.label}</span>
            </div>
            <p className="mt-0.5 text-[13.5px] text-zinc-500">{[c.phone, c.email].filter(Boolean).join(" · ") || "No contact info"}</p>
            <TagEditor tags={c.tags} onChange={(tags) => patch.mutate({ contactId: c.id, tags })} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} className={LIGHT_BTN}><Phone01 className="size-4" aria-hidden />Call</a>}
            <button type="button" onClick={focusComposer} className={LIGHT_BTN}>Text</button>
            {s.followUp !== "following" && s.followUp !== "no_contact" && <button type="button" onClick={() => setBooking(true)} className={LIGHT_BTN}>Book</button>}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-3">
          <p className="min-w-0 flex-1 text-[13.5px] text-zinc-700">{next.line}</p>
          {next.button}
        </div>
      </header>

      <div className="mt-5 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        {/* The conversation is the main thing */}
        <section className="min-w-0">
          <div className="mb-3 flex gap-1.5" role="radiogroup" aria-label="Show">
            <button type="button" role="radio" aria-checked={view === "conversation"} onClick={() => setView("conversation")} className={cx(pill(view === "conversation"), "h-9 px-3")}>Conversation</button>
            <button type="button" role="radio" aria-checked={view === "everything"} onClick={() => setView("everything")} className={cx(pill(view === "everything"), "h-9 px-3")}>Everything</button>
          </div>
          {view === "conversation" ? (
            <ThreadPanel
              teamId={teamId}
              contactId={c.id}
              conversationId={data.conversation?.id ?? null}
              firstName={c.firstName}
              phone={c.phone}
              email={c.email}
              smsBlocked={c.dndSettings.SMS.status === "active"}
              emailBlocked={c.dndSettings.Email.status === "active"}
              className="h-[calc(100dvh-24rem)] min-h-[460px]"
              autoFocus={!!params.get("compose")}
              composerRef={composerRef}
            />
          ) : (
            <div className="h-[calc(100dvh-24rem)] min-h-[460px] overflow-y-auto rounded-xl border border-zinc-200 bg-white px-4">
              <Timeline data={data} users={users} stages={stages} />
            </div>
          )}
        </section>

        {/* One rail, the facts */}
        <aside className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white xl:sticky xl:top-5">
          <Section title="About">
            <dl className="text-[13.5px]">
              {s.vehicle && <Fact k="Vehicle">{s.vehicle}</Fact>}
              {s.estimateId != null && (
                <Fact k="Estimate">
                  <Link to={`/estimates/${s.estimateId}`} className="inline-flex items-center gap-1 font-medium text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">#{s.estimateId}{c.customFields.find((f) => f.key === "price_range")?.value ? ` · ${c.customFields.find((f) => f.key === "price_range")?.value}` : ""}<ArrowUpRight className="size-3" aria-hidden /></Link>
                  {s.estimateStatus && <span className="ml-1.5 text-zinc-500">{s.estimateStatus === "InProgress" ? "in progress" : s.estimateStatus.toLowerCase()}</span>}
                </Fact>
              )}
              {c.estimateIds.length > 1 && <Fact k="Earlier estimates">{c.estimateIds.filter((id) => id !== s.estimateId).map((id) => <Link key={id} to={`/estimates/${id}`} className="mr-2 font-medium text-zinc-700 underline decoration-zinc-300 underline-offset-2">#{id}</Link>)}</Fact>}
              {c.customFields.find((f) => f.key === "insurance_carrier") && <Fact k="Insurance">{c.customFields.find((f) => f.key === "insurance_carrier")?.value}</Fact>}
              <Fact k="Owner">
                <TextSelect ariaLabel="Owner" value={c.assignedTo ?? ""} onChange={(v) => patch.mutate({ contactId: c.id, assignedTo: v || null })} options={[{ value: "", label: "Unassigned" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} className="-mr-2" />
              </Fact>
              {c.source && <Fact k="Came in via">{c.source}</Fact>}
              <Fact k="Added">{fmtDate(c.dateAdded)}</Fact>
            </dl>
          </Section>

          <Section title="Consent">
            <dl className="text-[13.5px]">
              <Fact k="Texts">{c.phone ? <Switch on={c.dndSettings.SMS.status !== "active"} onChange={(on) => patch.mutate({ contactId: c.id, dndSettings: { ...c.dndSettings, SMS: { status: on ? "inactive" : "active" } } })} /> : <span className="text-zinc-400">No number</span>}</Fact>
              <Fact k="Emails">{c.email ? <Switch on={c.dndSettings.Email.status !== "active"} onChange={(on) => patch.mutate({ contactId: c.id, dndSettings: { ...c.dndSettings, Email: { status: on ? "inactive" : "active" } } })} /> : <span className="text-zinc-400">No email</span>}</Fact>
            </dl>
          </Section>

          <DealSection teamId={teamId} data={data} stages={stages} />
          <TasksSection teamId={teamId} data={data} users={users} />
          <NotesSection teamId={teamId} data={data} users={users} />
          {data.appointments.length > 0 && (
            <Section title="Visits">
              <ul className="space-y-2 text-[13px]">
                {data.appointments.map((a) => (
                  <li key={a.id} className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-zinc-900">{a.title}</span>
                    <span className="shrink-0 text-zinc-500">{fmtDateTime(a.startTime)} · <span className={cx(a.appointmentStatus === "noshow" && "text-red-700", a.appointmentStatus === "showed" && "text-emerald-700")}>{APPT_WORD[a.appointmentStatus]}</span></span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </aside>
      </div>

      <BookDialog open={booking} teamId={teamId} contactName={name} onClose={() => setBooking(false)} onBook={async (startsAt, kind) => { if (await run({ kind: "book", contactId: c.id, startsAt, appointmentKind: kind }, "Booked")) setBooking(false) }} />
      {smsBlocked && null}
    </div>
  )
}

const APPT_WORD = { confirmed: "Booked", showed: "Showed", noshow: "No-show", cancelled: "Cancelled" } as const

function Section({ title, meta, action, children }: { title: string; meta?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3 pb-2">
        <h3 className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">{title}{meta && <span className="ml-1.5 font-semibold text-zinc-400">· {meta}</span>}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}
function Fact({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-zinc-500">{k}</dt>
      <dd className="min-w-0 text-right text-zinc-900">{children}</dd>
    </div>
  )
}
function Switch({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className={cx("h-7 rounded-full border px-2.5 text-[12px] font-semibold transition-colors", on ? "border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50" : "border-red-200 bg-red-50 text-red-700")}>
      {on ? "Reachable" : "Opted out"}
    </button>
  )
}
const LINK_BTN = "text-[12.5px] font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"

function TagEditor({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [v, setV] = useState("")
  const add = () => { const t = v.trim().toLowerCase(); if (!t || tags.includes(t)) { setV(""); return }; onChange([...tags, t]); setV("") }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {tags.map((t) => <TagChip key={t} tag={t} onRemove={() => onChange(tags.filter((x) => x !== t))} />)}
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add() }} onBlur={add} placeholder="+ tag" aria-label="Add a tag" className="h-6 w-16 rounded-md border border-dashed border-zinc-300 bg-transparent px-1.5 text-[12px] text-zinc-800 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none" />
    </div>
  )
}

function DealSection({ teamId, data, stages }: { teamId: number | string; data: GhlContactDetail; stages: Array<{ id: string; name: string }> }) {
  const patch = usePatchOpportunity(teamId)
  const o = data.opportunities[0]
  if (!o) return null
  return (
    <Section title="Deal" meta={data.opportunities.length > 1 ? `${data.opportunities.length}` : undefined}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[16px] font-semibold tabular-nums text-zinc-900">{usd(o.monetaryValue)}</p>
        {o.status !== "open" && <p className={cx("text-[13px] font-semibold", o.status === "won" ? "text-emerald-700" : "text-zinc-500")}>{o.status === "won" ? "Won" : o.status === "lost" ? "Lost" : "Abandoned"} · {fmtDate(o.lastStatusChangeAt)}</p>}
      </div>
      {o.status === "open" ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <TextSelect ariaLabel="Stage" value={o.pipelineStageId} onChange={(v) => patch.mutate({ id: o.id, pipelineStageId: v })} options={stages.map((s) => ({ value: s.id, label: s.name }))} className="-ml-2" />
          <button type="button" onClick={() => patch.mutate({ id: o.id, status: "won" })} className="ml-auto h-8 rounded-md px-2 text-[12.5px] font-semibold text-emerald-700 hover:bg-emerald-50">Won</button>
          <button type="button" onClick={() => patch.mutate({ id: o.id, status: "lost" })} className="h-8 rounded-md px-2 text-[12.5px] font-semibold text-zinc-500 hover:bg-zinc-100">Lost</button>
        </div>
      ) : (
        <button type="button" onClick={() => patch.mutate({ id: o.id, status: "open" })} className={cx(LINK_BTN, "mt-1")}>Reopen</button>
      )}
    </Section>
  )
}

function TasksSection({ teamId, data, users }: { teamId: number | string; data: GhlContactDetail; users: ReturnType<typeof useGhlUsers>["data"] }) {
  const create = useCreateTask(teamId)
  const patchTask = usePatchTask(teamId)
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState("")
  const [due, setDue] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10))
  const open = data.tasks.filter((t) => !t.completed)
  const done = data.tasks.filter((t) => t.completed).slice(0, 3)
  const submit = async () => {
    if (!title.trim()) return
    await create.mutateAsync({ contactId: data.contact.id, title: title.trim(), dueDate: new Date(`${due}T09:00:00`).toISOString(), assignedTo: data.contact.assignedTo })
    setTitle(""); setAdding(false)
  }
  return (
    <Section title="Tasks" meta={open.length ? `${open.length} open` : undefined} action={<button type="button" onClick={() => setAdding((v) => !v)} className={LINK_BTN}>{adding ? "Cancel" : "Add a task"}</button>}>
      {adding && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void submit() }} placeholder="What needs doing?" aria-label="Task" className={cx(INPUT, "h-9 w-full")} />
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" className={cx(INPUT, "h-9 flex-1")} />
          <button type="button" onClick={() => void submit()} disabled={!title.trim() || create.isPending} className={cx(DARK_BTN, "h-9")}>Add</button>
        </div>
      )}
      {open.length === 0 && done.length === 0 && !adding && <p className="text-[13px] text-zinc-500">Nothing open.</p>}
      <ul className="-mx-2">
        {[...open, ...done].map((t) => {
          const overdue = !t.completed && new Date(t.dueDate).getTime() < Date.now()
          return (
            <li key={t.id} className="flex items-start gap-0.5">
              <Check checked={t.completed} onChange={() => patchTask.mutate({ id: t.id, completed: !t.completed })} label={t.completed ? `Reopen ${t.title}` : `Complete ${t.title}`} />
              <div className="min-w-0 flex-1 py-1">
                <p className={cx("text-[13.5px]", t.completed ? "text-zinc-400 line-through" : "text-zinc-900")}>{t.title}</p>
                <p className={cx("text-[12px]", overdue ? "font-semibold text-red-700" : "text-zinc-500")}>{t.completed ? `Done ${relTime(t.completedAt)}` : overdue ? `Overdue · ${relTime(t.dueDate)}` : fmtDate(t.dueDate)} · {userName(users, t.assignedTo)}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

function NotesSection({ teamId, data, users }: { teamId: number | string; data: GhlContactDetail; users: ReturnType<typeof useGhlUsers>["data"] }) {
  const create = useCreateNote(teamId)
  const [adding, setAdding] = useState(false)
  const [body, setBody] = useState("")
  const submit = async () => { if (!body.trim()) return; await create.mutateAsync({ contactId: data.contact.id, body: body.trim() }); setBody(""); setAdding(false) }
  return (
    <Section title="Notes" action={<button type="button" onClick={() => setAdding((v) => !v)} className={LINK_BTN}>{adding ? "Cancel" : "Add a note"}</button>}>
      {adding && (
        <div className="mb-2">
          <textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit() }} placeholder="Write it down…" aria-label="Note" rows={2} className={cx(INPUT, "h-auto w-full resize-none py-2")} />
          <div className="mt-1.5 flex justify-end"><button type="button" onClick={() => void submit()} disabled={!body.trim() || create.isPending} className={cx(DARK_BTN, "h-9")}>Save</button></div>
        </div>
      )}
      {data.notes.length === 0 && !adding && <p className="text-[13px] text-zinc-500">No notes.</p>}
      <ul className="space-y-2">
        {data.notes.map((n) => (
          <li key={n.id}>
            <p className="whitespace-pre-wrap text-[13.5px] text-zinc-900">{n.body}</p>
            <p className="text-[12px] text-zinc-500">{userName(users, n.userId)} · {relTime(n.dateAdded)}</p>
          </li>
        ))}
      </ul>
    </Section>
  )
}

interface Event { id: string; at: string; title: React.ReactNode; body?: string | null; by?: string | null; inbound?: boolean; icon: React.ReactNode }

function Timeline({ data, users, stages }: { data: GhlContactDetail; users: ReturnType<typeof useGhlUsers>["data"]; stages: Array<{ id: string; name: string }> }) {
  const events = useMemo<Event[]>(() => {
    const out: Event[] = []
    const first = data.contact.firstName
    for (const m of data.messages) {
      const call = m.meta?.call
      out.push({
        id: m.id, at: m.dateAdded, inbound: m.direction === "inbound", icon: <ChannelIcon type={m.type} className="size-3.5 text-zinc-500" />,
        title: (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-zinc-900">{m.direction === "inbound" ? `${first} wrote` : m.type === "TYPE_CALL" ? "Call" : m.type === "TYPE_EMAIL" ? "Email" : "Text"}</span>
            {m.type === "TYPE_CALL" && call ? <span className="text-zinc-500">{call.outcome === "answered" ? `answered · ${Math.round(call.duration / 60)} min` : call.outcome.replace("_", " ")}</span> : m.direction === "outbound" ? <StatusPill status={m.status} /> : null}
            {m.meta?.email?.clicked && <span className="text-[11px] font-semibold text-emerald-700">clicked</span>}
          </span>
        ),
        body: m.type === "TYPE_EMAIL" && m.subject ? `${m.subject}\n${m.body}` : m.body,
        by: m.userId ? userName(users, m.userId) : m.direction === "outbound" ? "Automated" : null,
      })
    }
    for (const n of data.notes) out.push({ id: n.id, at: n.dateAdded, icon: <File02 className="size-3.5 text-zinc-500" aria-hidden />, title: <span className="font-semibold text-zinc-900">Note</span>, body: n.body, by: userName(users, n.userId) })
    for (const t of data.tasks) {
      out.push({ id: `${t.id}-add`, at: t.dateAdded, icon: <Flag01 className="size-3.5 text-zinc-500" aria-hidden />, title: <span className="font-semibold text-zinc-900">Task</span>, body: t.title, by: userName(users, t.assignedTo) })
      if (t.completed && t.completedAt) out.push({ id: `${t.id}-done`, at: t.completedAt, icon: <Flag01 className="size-3.5 text-zinc-500" aria-hidden />, title: <span className="font-semibold text-zinc-900">Task done</span>, body: t.title })
    }
    for (const a of data.appointments) out.push({ id: a.id, at: a.startTime, icon: <CalendarDate className="size-3.5 text-zinc-500" aria-hidden />, title: <span className="font-semibold text-zinc-900">{APPT_WORD[a.appointmentStatus]}</span>, body: a.title })
    for (const o of data.opportunities) {
      out.push({ id: `${o.id}-add`, at: o.dateAdded, icon: <span className="text-[10px] font-bold text-zinc-500">$</span>, title: <span className="font-semibold text-zinc-900">Deal opened · {usd(o.monetaryValue)}</span>, body: o.name })
      if (o.status !== "open") out.push({ id: `${o.id}-status`, at: o.lastStatusChangeAt, icon: <span className="text-[10px] font-bold text-zinc-500">$</span>, title: <span className={cx("font-semibold", o.status === "won" ? "text-emerald-700" : "text-zinc-900")}>{o.status === "won" ? "Won" : o.status === "lost" ? "Lost" : "Abandoned"} · {usd(o.monetaryValue)}</span> })
      else if (o.lastStageChangeAt !== o.dateAdded) out.push({ id: `${o.id}-stage`, at: o.lastStageChangeAt, icon: <span className="text-[10px] font-bold text-zinc-500">$</span>, title: <span className="font-semibold text-zinc-900">Moved to {stages.find((s) => s.id === o.pipelineStageId)?.name ?? "a new stage"}</span> })
    }
    return out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  }, [data, users, stages])

  if (events.length === 0) return <Empty>Nothing has happened with this contact yet.</Empty>
  return (
    <ol className="divide-y divide-zinc-100">
      {events.map((e) => (
        <li key={e.id} className={cx("flex gap-3 py-3", e.inbound && "-mx-4 bg-zinc-50/80 px-4")}>
          <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-white ring-1 ring-zinc-200">{e.icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-[13.5px]">{e.title}</div>
              <time className="shrink-0 text-[12px] text-zinc-400" dateTime={e.at} title={fmtDateTime(e.at)}>{relTime(e.at)}</time>
            </div>
            {e.body && <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-relaxed text-zinc-700">{e.body}</p>}
            {e.by && <p className="mt-1 text-[12px] text-zinc-400">{e.by}</p>}
          </div>
        </li>
      ))}
    </ol>
  )
}
